import type { NewsStory } from './newsStory';

const STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'as',
  'by',
  'with',
  'from',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'has',
  'have',
  'had',
  'will',
  'says',
  'said',
  'after',
  'over',
  'into',
  'about',
  'its',
  'it',
  'that',
  'this',
  'not',
  'no',
  'new',
  'how',
  'why',
  'what',
  'when',
  'who',
  'up',
  'out',
  'can',
  'all',
  'his',
  'her',
  'their',
  'our',
  'your',
  'my',
  'more',
  'than',
  'just',
  'also',
  'amid',
  'live',
  'video',
]);

const TITLE_SUFFIX_RE =
  /\s*[-–—|]\s*(BBC News|BBC|the Guardian|The Guardian|El País|El Pais|Reuters|AP|AFP)\s*$/i;

export type DedupedNewsResult = {
  stories: NewsStory[];
  duplicatesRemoved: number;
};

/**
 * Collapse the same story reported by multiple outlets.
 * Keeps the earliest feedRank entry per duplicate cluster (per source tie-break: lower feedRank wins).
 */
export function dedupeNewsStories(stories: readonly NewsStory[]): DedupedNewsResult {
  const kept: NewsStory[] = [];
  let duplicatesRemoved = 0;

  for (const story of stories) {
    const dupIndex = kept.findIndex(k => areDuplicateStories(k, story));
    if (dupIndex < 0) {
      kept.push(story);
      continue;
    }
    duplicatesRemoved += 1;
    const existing = kept[dupIndex]!;
    if (shouldPreferStory(story, existing)) {
      kept[dupIndex] = story;
    }
  }

  return { stories: kept, duplicatesRemoved };
}

/** Merge duplicate metadata for ranked display. */
export function collectAlsoFromLabels(
  story: NewsStory,
  allStories: readonly NewsStory[]
): string[] {
  const labels = new Set<string>();
  for (const other of allStories) {
    if (other === story) continue;
    if (areDuplicateStories(story, other)) labels.add(other.sourceLabel);
  }
  return [...labels].sort();
}

function shouldPreferStory(candidate: NewsStory, incumbent: NewsStory): boolean {
  if (candidate.feedRank !== incumbent.feedRank) {
    return candidate.feedRank < incumbent.feedRank;
  }
  return candidate.sourceId.localeCompare(incumbent.sourceId) < 0;
}

export function areDuplicateStories(a: NewsStory, b: NewsStory): boolean {
  if (a.link && b.link && normalizeStoryUrl(a.link) === normalizeStoryUrl(b.link)) {
    return true;
  }
  return titleSimilarity(a.title, b.title) >= 0.62;
}

export function normalizeStoryUrl(url: string): string {
  try {
    const u = new URL(url.trim());
    u.hash = '';
    u.search = '';
    let path = u.pathname.replace(/\/+$/, '');
    if (u.hostname.includes('reddit.com') && path.includes('/comments/')) {
      const m = path.match(/\/comments\/([a-z0-9]+)/i);
      if (m) return `reddit:${m[1]!.toLowerCase()}`;
    }
    if (u.hostname.includes('news.ycombinator.com')) {
      const id = new URL(url.trim()).searchParams.get('id');
      if (id) return `hn:${id}`;
    }
    return `${u.hostname}${path}`.toLowerCase();
  } catch {
    return url.trim().toLowerCase();
  }
}

export function normalizeTitleForMatch(title: string): string {
  return title
    .replace(TITLE_SUFFIX_RE, '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, "'")
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function titleTokens(title: string): Set<string> {
  const tokens = new Set<string>();
  for (const word of normalizeTitleForMatch(title).split(' ')) {
    if (word.length < 3 || STOPWORDS.has(word)) continue;
    tokens.add(word);
  }
  return tokens;
}

export function titleSimilarity(a: string, b: string): number {
  const ta = titleTokens(a);
  const tb = titleTokens(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const t of ta) {
    if (tb.has(t)) inter += 1;
  }
  const union = ta.size + tb.size - inter;
  return union === 0 ? 0 : inter / union;
}
